// src/redux/sagas/restaurantSaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import { buildRestaurantUrl } from '../../utils/domainUtils';
import {setRestaurantId } from '../../api/tokenUtils';
import {
  fetchRestaurantsRequest,
  fetchRestaurantsSuccess,
  fetchRestaurantsFailure,
  selectRestaurant,
} from '../slices/restaurantSlice';
import { checkAndValidateEditControl } from '../../utils/editControlUtils';
import { fetchSiteContentRequest } from '../slices/siteContentSlice';
import { fetchSubscriptionRequest } from '../slices/subscriptionSlice';
import { shouldSkipApiCalls } from '../../utils/appConfig';
import { transformFranchisesToRestaurants } from '../../utils/franchiseTransformer';

function* fetchRestaurantsSaga(): Generator<
  unknown,
  void,
  AxiosResponse<any>
> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const url = buildRestaurantUrl();
    const response = yield call(request<any>, {
      method: 'GET',
      url,
      requiresRestaurantId: false,
    });
    
    // Extract franchise data from new API response format
    const franchiseList = response?.data?.data || [];
    
    // Transform franchise data to Restaurant type using utility
    const restaurantsList = transformFranchisesToRestaurants(franchiseList);
    
    // Store all restaurants in Redux
    yield put(fetchRestaurantsSuccess(restaurantsList));
    
    // Auto-select if only one restaurant (regardless of saved ID)
    if (restaurantsList?.length === 1) {
      const singleRestaurant = restaurantsList[0];
      
      if (singleRestaurant?.restaurant_id) {
        // Always set/update the restaurant ID for single restaurant
        setRestaurantId(singleRestaurant?.restaurant_id);
        yield put(selectRestaurant(singleRestaurant?.restaurant_id));
        
        // ALWAYS fetch site content for single restaurant
        yield put(fetchSiteContentRequest());
        
        // Fetch subscription status after site content
        yield put(fetchSubscriptionRequest());
        
        // Validate edit control after selecting single restaurant
        checkAndValidateEditControl(singleRestaurant.restaurant_id);
      }
    }
    // Removed auto-selection of saved restaurant to ensure location selector always shows
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error?.message : 'Failed to fetch restaurants';
    yield put(fetchRestaurantsFailure(errorMessage));
  }
}



export function* watchRestaurantSaga() {
  yield takeEvery(fetchRestaurantsRequest.type, fetchRestaurantsSaga);
}
